/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useState, useMemo, useCallback } from "react";

import { useNavigate } from "react-router-dom";

import { ROUTES } from "Router";
import { checkRouteValidity, getEventId } from "Router/RoutesUtils";
import sleep from "utils/sleep";
import { getTimezoneDetails } from "utils/timezoneUtils";

import useFetcher from "hooks/useFetcher";
import { persistLoginSession } from "lib/v2/LoginAPI";
import { getAllPrimaryEvents } from "lib/v3/primaryEventsApi";
import { Event } from "../../models/Event";

const MINUTE = 60 * 1000;

import React from "react";

import { useAuth, useRole } from "state";
import EventsContext from "./context";
import { useEnvs } from "state/EnvironmentsContext/EnvContext";

const PrimaryEventsProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { isLoggedIn, isLoading: isAuthenticating } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isSwitchingEvent, setIsSwitchingEvent] = useState(false);
  const [hasSelectedEvent, setHasSelectedEvent] = useState(false);
  const { setPermissionsAndPages } = useRole();
  const { EVENT_LOCATION_DETAILS } = useEnvs((state) => state);

  const { isLoading, refetch } = useFetcher(getAllPrimaryEvents, {
    onSuccess: (data: Event[]) => {
      setEvents(data);
    },
    enabled: isLoggedIn,
    refetchOnConditions: [isLoggedIn],
  });

  const getEventCoordinates = () => {
    const lat = selectedEvent?.lat;
    const lng = selectedEvent?.lng;
    if (lat && lng) {
      return { lat: +lat, lng: +lng };
    }
    // fallback
    return EVENT_LOCATION_DETAILS;
  };

  const eventCoordinates = useMemo(getEventCoordinates, [selectedEvent]);

  const getEvent = (id: string) => {
    return events.find((event) => event.id === Number(id));
  };

  const handleSelectEvent = async (event: Event) => {
    setIsSwitchingEvent(true);
    window.location.replace(ROUTES.HOME + `${event.id}`);
    await sleep(500);
    const dbTimezone = event.time_zone;
    const timezone = getTimezoneDetails(dbTimezone);
    setSelectedEvent({ ...event, timezone });
    localStorage.setItem("eventId", JSON.stringify(event.id || ""));
    const { data, status } = await persistLoginSession();
    if (status !== "FAILED") {
      setPermissionsAndPages(data.user);
    }

    setHasSelectedEvent(true);
    setIsSwitchingEvent(false);
  };

  const handleRemoveEvent = (route?: string) => {
    const { routeHasEventId } = checkRouteValidity();
    if (routeHasEventId) {
      window.location.replace(route || ROUTES.BASE);
    } else {
      setSelectedEvent(null);
      setHasSelectedEvent(false);
      navigate(route || ROUTES.BASE);
    }
  };

  const persistEvent = useCallback(() => {
    const eventId = getEventId();
    if (eventId) {
      const event = events.find((e) => e?.id === +eventId);
      if (event) {
        const dbTimezone = event.time_zone;
        const timezone = getTimezoneDetails(dbTimezone);
        setSelectedEvent({ ...event, timezone });
        setHasSelectedEvent(true);
      }
    }
  }, [events]);

  useEffect(() => {
    if (isLoggedIn && !hasSelectedEvent && events.length > 0) {
      persistEvent();
    }
  }, [isLoggedIn, isAuthenticating, events, hasSelectedEvent, persistEvent]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (hasSelectedEvent && selectedEvent && selectedEvent.id) {
        const { timezone } = selectedEvent;

        const newTimezoneDetails = getTimezoneDetails(
          timezone?.timezone as Parameters<typeof getTimezoneDetails>[0],
        );

        setSelectedEvent({
          ...selectedEvent,
          timezone: newTimezoneDetails,
        });
      }
    }, MINUTE);

    return () => clearInterval(interval);
  }, [hasSelectedEvent, selectedEvent]);

  const context = {
    events,
    setEvents,
    selectedEvent,
    setSelectedEvent, // Add this line to match context shape
    handleSelectEvent,
    handleRemoveEvent,
    isLoading,
    hasSelectedEvent,
    isSwitchingEvent,
    refetchEvents: refetch,
    getEventCoordinates,
    eventCoordinates,
    getEvent,
  };

  return (
    <EventsContext.Provider value={context} test-id="cI-fFV_r">
      {children}
    </EventsContext.Provider>
  );
};

export default PrimaryEventsProvider;
